/**
 * The chart contract on the client: validate, parse a frame or a fence, split an answer.
 *
 * PURE — no React. Unit-tested in `tests/brainChart.test.ts`. Mirrors the backend clamp
 * (`services/charts/spec.py`); anything outside it is dropped, never drawn and never shown as
 * raw JSON.
 */

import { asRecord } from '@/components/diagram/spec';
import type { DiagramSource } from '@/components/diagram/types';

import type { BrainChartData, ChartDatum, ChartKind, ChartSpec } from './types';

export const CHART_KINDS: readonly ChartKind[] = [
  'bar',
  'horizontal_bar',
  'stacked_bar',
  'line',
  'area',
  'pie',
  'donut',
];
export const CHART_MIN_CATEGORIES = 2;
export const CHART_MAX_CATEGORIES = 40;
export const CHART_MAX_SERIES = 6;
export const CHART_MAX_SLICES = 8;
const MAX_SOURCES = 4;

const clip = (v: unknown, max: number): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, max) : null;
};

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** A chart spec the renderer can draw, from the wire's snake_case or camelCase — or null. */
export function validateChartSpec(raw: unknown): ChartSpec | null {
  const r = asRecord(raw);
  if (!r) return null;
  const kind = typeof r.kind === 'string' ? (r.kind.trim().toLowerCase() as ChartKind) : null;
  if (!kind || !CHART_KINDS.includes(kind)) return null;
  const title = clip(r.title, 80);
  if (!title) return null;

  const series = Array.isArray(r.series)
    ? r.series.map((s) => clip(s, 40)).filter((s): s is string => !!s)
    : [];
  if (!series.length) series.push(clip(r.y_label ?? r.yLabel, 40) ?? 'Value');
  if (series.length > CHART_MAX_SERIES) return null;

  if (!Array.isArray(r.data)) return null;
  const data: ChartDatum[] = [];
  const seen = new Set<string>();
  for (const item of r.data.slice(0, CHART_MAX_CATEGORIES)) {
    const row = asRecord(item);
    const label = row ? clip(row.label, 40) : null;
    const values = row && Array.isArray(row.values) ? row.values.map(finite) : null;
    if (!label || !values || values.length !== series.length || values.some((v) => v === null)) return null;
    if (seen.has(label.toLowerCase())) return null;
    seen.add(label.toLowerCase());
    data.push({ label, values: values as number[] });
  }
  if (data.length < CHART_MIN_CATEGORIES) return null;

  const partOfWhole = kind === 'pie' || kind === 'donut';
  if (partOfWhole) {
    // The server redraws these as bars; a hand-written spec that slips through still must not
    // render a pie that lies.
    if (series.length !== 1 || data.length > CHART_MAX_SLICES || data.some((d) => d.values[0] < 0))
      return null;
  }

  const caption = clip(r.caption, 240);
  const unit = clip(r.unit, 16);
  const xLabel = clip(r.x_label ?? r.xLabel, 40);
  const yLabel = clip(r.y_label ?? r.yLabel, 40);
  return {
    kind,
    title,
    series,
    data,
    ...(caption ? { caption } : {}),
    ...(unit ? { unit } : {}),
    ...(xLabel ? { xLabel } : {}),
    ...(yLabel ? { yLabel } : {}),
  };
}

function cleanSources(raw: unknown): DiagramSource[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: DiagramSource[] = [];
  for (const item of raw.slice(0, MAX_SOURCES)) {
    const s = asRecord(item);
    const title = s ? clip(s.title, 120) : null;
    if (!title) continue;
    const kind = s ? clip(s.kind, 40) : null;
    out.push({ title, ...(kind ? { kind } : {}) });
  }
  return out.length ? out : undefined;
}

/**
 * The chart a Brain drew, from a `chart` frame (`{ spec, sources, basis }`), a
 * `turn_complete.chart` payload, or a fenced spec (a spec with `sources` / `basis` beside it) —
 * validated; null otherwise.
 */
export function parseBrainChart(raw: unknown): BrainChartData | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const r = asRecord(value);
  if (!r) return null;
  const spec = validateChartSpec(asRecord(r.spec) ?? r);
  if (!spec) return null;
  const sources = cleanSources(r.sources);
  const basis = clip(r.basis, 200);
  return { spec, ...(sources ? { sources } : {}), ...(basis ? { basis } : {}) };
}

// ── fences ─────────────────────────────────────────────────────────────────────────────────────

export const CHART_FENCE = 'chart';

const FENCE_RE = /```chart[ \t]*\n([\s\S]*?)```/g;
const OPEN_FENCE_RE = /```chart[ \t]*(\n|$)(?![\s\S]*?```)/;

/**
 * A Brain answer's markdown → its prose (```chart fences removed) and the charts it carried. A
 * fence that does not validate is dropped; an UNTERMINATED fence — the reply is still streaming —
 * is cut off, so the reader never sees the JSON being typed.
 */
export function splitBrainCharts(text: string): { text: string; charts: BrainChartData[] } {
  if (!text || !text.includes('```' + CHART_FENCE)) return { text, charts: [] };
  const charts: BrainChartData[] = [];
  const prose: string[] = [];
  let last = 0;
  FENCE_RE.lastIndex = 0;
  for (let m = FENCE_RE.exec(text); m; m = FENCE_RE.exec(text)) {
    prose.push(text.slice(last, m.index));
    const chart = parseBrainChart(m[1].trim());
    if (chart) charts.push(chart);
    last = m.index + m[0].length;
  }
  let tail = text.slice(last);
  const open = OPEN_FENCE_RE.exec(tail);
  if (open) tail = tail.slice(0, open.index);
  prose.push(tail);
  return {
    text: prose
      .map((p) => p.trim())
      .filter(Boolean)
      .join('\n\n'),
    charts,
  };
}

// ── formatting ─────────────────────────────────────────────────────────────────────────────────

/** A value as a reader reads it: thousands grouped, shares to one decimal, the unit kept. */
export function formatChartValue(value: number, unit?: string): string {
  const abs = Math.abs(value);
  // Shares read as one steady decimal (49.1%, 9.0%); other values lose decimals as they grow.
  const digits = unit === '%' ? 1 : abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  const n = value.toLocaleString('en-US', {
    maximumFractionDigits: digits,
    minimumFractionDigits: unit === '%' ? 1 : 0,
  });
  if (!unit) return n;
  return unit === '%' ? `${n}%` : `${n} ${unit}`;
}

/** Each category's share of the chart's total, for a part-of-whole chart's labels. */
export function shareOf(data: ChartDatum[], index: number): number {
  const total = data.reduce((sum, d) => sum + d.values[0], 0);
  return total > 0 ? (data[index].values[0] * 100) / total : 0;
}

/**
 * The charts a message shows: the streamed / patched one first, then any fenced in the stored
 * text that isn't that same chart — the transcript may carry the fence the frame already
 * delivered, and that is one chart, not two.
 */
export function collectBrainCharts(
  primary: BrainChartData | null | undefined,
  fenced: readonly BrainChartData[],
): readonly BrainChartData[] {
  if (!primary) return fenced;
  return [primary, ...fenced.filter((c) => c.spec.title !== primary.spec.title)];
}
